import { Button, Typography } from '@execonline-inc/execonline-ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { T, TranslationsContext, translation } from '../../../../../../Translations';
import { AnchoredModal } from '../components/AnchoredModal';

// DVLP-7971: the same anchored confirmation the section delete uses
// (Section/DeleteSectionModal), so removing a logo asks before it acts.

interface ChildrenProps {
  onOpen: () => void;
}

interface Props {
  logoHref: string;
  onConfirm: () => void;
  children: (props: ChildrenProps) => React.ReactNode;
}

function RemoveLogoModal({ logoHref, onConfirm, children }: Props) {
  const ts = useContext(TranslationsContext);
  return (
    <AnchoredModal
      placement="bottom-end"
      closeOnContentClick={false}
      showArrow={false}
      aria-labelledby="remove-logo-modal-title"
      children={({ onClose }) => (
        <div className="relative px-5 pt-2 pb-5 w-[320px]">
          <button
            type="button"
            onClick={onClose}
            className="absolute -top-1 -right-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={translation('Close dialog')(ts)}
          >
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>

          <div className="mb-3">
            <Typography id="remove-logo-modal-title" variant="h6" fontWeight="semibold">
              <T kind="Remove Logo" />
            </Typography>
          </div>

          <hr className="border-border mb-4" />

          <div className="mb-4 flex justify-center">
            <img src={logoHref} alt="" className="max-h-24 max-w-full object-contain rounded-md" />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="text"
              color="secondary"
              onClick={onClose}
              data-intercom-target="builder-v2-remove-logo-cancel-button"
            >
              <T kind="Cancel" />
            </Button>
            <Button
              color="error"
              onClick={() => {
                onConfirm();
                onClose();
              }}
              data-intercom-target="builder-v2-remove-logo-confirm-button"
            >
              <T kind="Remove" />
            </Button>
          </div>
        </div>
      )}
      renderTrigger={(ref, getReferenceProps) => {
        const props = getReferenceProps();
        const onClick = props.onClick as () => void;
        return <div ref={ref}>{children({ onOpen: onClick })}</div>;
      }}
    />
  );
}

export default observer(RemoveLogoModal);
